import React, { useRef, useEffect, useState } from 'react';
import { Search, X } from 'lucide-react';
import { useGlobalSearch } from './useGlobalSearch';
import SearchAutocomplete from './SearchAutocomplete';

interface GlobalSearchBarProps {
  className?: string;
}

const GlobalSearchBar: React.FC<GlobalSearchBarProps> = ({ className }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const {
    query,
    setQuery,
    results,
    isLoading,
    isOpen,
    setIsOpen,
    selectedIndex,
    handleKeydown,
    selectResult,
    clearSearch,
  } = useGlobalSearch();

  // Handle clicks outside to close search
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [setIsOpen]);

  // Handle escape key to close search
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
        setIsOpen(false);
        if (inputRef.current) {
          inputRef.current.blur();
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [setIsOpen]);

  const handleFocus = () => {
    setIsExpanded(true);
    if (query.length >= 2) {
      setIsOpen(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleClear = () => {
    clearSearch();
    setIsExpanded(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleSearchIconClick = () => {
    if (!isExpanded) {
      setIsExpanded(true);
      // Focus input after animation starts
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 150);
    } else if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div 
      ref={containerRef}
      className={`relative ${className}`}
    >
      {/* Search container with animation */}
      <div 
        className={`flex items-center bg-white border border-gray-200 rounded-full transition-all duration-300 ease-in-out ${
          isExpanded 
            ? 'w-80 shadow-md' 
            : 'w-10 hover:shadow-sm'
        }`}
      >
        {/* Search icon */}
        <button
          onClick={handleSearchIconClick}
          className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          aria-label="Search"
        >
          <Search className="w-5 h-5" />
        </button>

        {/* Search input */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeydown}
          placeholder="Search artists and aligned contributors..."
          className={`flex-1 py-2 px-2 bg-transparent border-none outline-none text-sm placeholder-gray-400 transition-all duration-300 ${
            isExpanded ? 'opacity-100 w-full' : 'opacity-0 w-0'
          }`}
          style={{ 
            width: isExpanded ? 'auto' : '0',
            overflow: 'hidden'
          }}
        />

        {/* Clear button */}
        {isExpanded && query && (
          <button
            onClick={handleClear}
            className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search autocomplete dropdown */}
      {isExpanded && (
        <SearchAutocomplete
          isOpen={isOpen}
          results={results}
          isLoading={isLoading}
          selectedIndex={selectedIndex}
          onSelect={selectResult}
          query={query}
        />
      )}
    </div>
  );
};

export default GlobalSearchBar;